import { useState } from "react";
import { useNavigate } from "react-router";
import { PageHeader } from "../../components/shared/PageHeader";
import { Check, ArrowLeft, ArrowRight, Send, GitMerge, LayoutTemplate, BarChart2, Lock } from "lucide-react";
import { cn } from "@nthamizh/ui";
import { useInviteUser, useEnterpriseRoles, useEnterpriseModules } from "@platform/hooks";
import { useToast } from "@nthamizh/ui";

const STEPS = ["User details", "App access", "Assign roles", "Review and send"];

const MODULE_META: Record<string, { icon: React.ComponentType<{ className?: string }>; iconBg: string }> = {
  migrate: { icon: GitMerge, iconBg: "bg-indigo-600" },
  design: { icon: LayoutTemplate, iconBg: "bg-cyan-600" },
  analytics: { icon: BarChart2, iconBg: "bg-gray-400" },
};

interface AppModule { moduleId: string; moduleKey: string; moduleName: string; isAvailable: boolean; isEnabled: boolean }
interface Role { id: string; key: string; name: string; description: string | null; moduleKey: string | null; permissions: Record<string, Record<string, boolean>> }

export function InviteUserPage() {
  const navigate = useNavigate();
  const { data: modules = [] } = useEnterpriseModules();
  const { data: roles = [] } = useEnterpriseRoles();
  const invite = useInviteUser();
  const { toast } = useToast();

  const [step, setStep] = useState(0);
  const [form, setForm] = useState({ firstName: "", lastName: "", email: "", jobTitle: "" });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [selectedApps, setSelectedApps] = useState<string[]>([]);
  const [selectedRoles, setSelectedRoles] = useState<string[]>([]);
  const [success, setSuccess] = useState(false);

  const typedModules = modules as AppModule[];
  const typedRoles = roles as Role[];

  const availableRoles = typedRoles.filter((r) =>
    r.key === "enterprise_admin" ||
    (r.moduleKey && selectedApps.includes(r.moduleKey))
  );

  const validateStep1 = () => {
    const e: Record<string, string> = {};
    if (!form.firstName.trim()) e.firstName = "First name is required.";
    if (!form.lastName.trim()) e.lastName = "Last name is required.";
    if (!form.email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = "Enter a valid email address.";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const next = () => {
    if (step === 0 && !validateStep1()) return;
    if (step === 1 && selectedApps.length === 0) { setErrors({ apps: "Select at least one app." }); return; }
    if (step === 2 && selectedRoles.length === 0) { setErrors({ roles: "Select at least one role." }); return; }
    setErrors({});
    setStep((s) => s + 1);
  };

  const back = () => { setErrors({}); setStep((s) => s - 1); };

  const toggleApp = (key: string) => {
    setSelectedApps((prev) => prev.includes(key) ? prev.filter((a) => a !== key) : [...prev, key]);
    setSelectedRoles([]);
    setErrors({});
  };

  const toggleRole = (id: string) => {
    setSelectedRoles((prev) => prev.includes(id) ? prev.filter((r) => r !== id) : [...prev, id]);
    setErrors({});
  };

  const handleSubmit = async () => {
    try {
      await invite.mutateAsync({
        firstName: form.firstName,
        lastName: form.lastName,
        email: form.email,
        jobTitle: form.jobTitle || undefined,
        appKeys: selectedApps,
        roleIds: selectedRoles,
      });
      setSuccess(true);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : "Failed to send invite";
      toast({ title: message, variant: "error" });
    }
  };

  const reset = () => {
    setStep(0); setForm({ firstName: "", lastName: "", email: "", jobTitle: "" });
    setSelectedApps([]); setSelectedRoles([]); setSuccess(false);
  };

  if (success) {
    return (
      <div className="max-w-lg mx-auto text-center py-16 space-y-4">
        <div className="w-14 h-14 rounded-full bg-emerald-100 flex items-center justify-center mx-auto">
          <Check className="w-7 h-7 text-emerald-600" />
        </div>
        <h2 className="text-lg font-semibold text-ds-text">User added</h2>
        <p className="text-sm text-ds-text-secondary max-w-xs mx-auto">
          <strong>{form.email}</strong> has been added to the enterprise and their roles have been assigned.
        </p>
        <div className="flex gap-3 justify-center pt-2">
          <button onClick={reset} className="px-4 py-2 rounded-lg border border-ds-border text-sm text-ds-text hover:bg-ds-bg">Invite another</button>
          <button onClick={() => navigate("/enterprise/users")} className="px-4 py-2 rounded-lg bg-indigo-600 text-white text-sm font-medium hover:bg-indigo-700">Back to users</button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-2xl space-y-6">
      <PageHeader title="Add user" subtitle={`Step ${step + 1} of ${STEPS.length}`} />

      {/* Stepper */}
      <div className="flex items-center">
        {STEPS.map((s, i) => (
          <div key={s} className="flex items-center flex-1 last:flex-none">
            <div className="flex items-center gap-2">
              <div className={cn("w-7 h-7 rounded-full flex items-center justify-center text-xs font-semibold",
                i < step ? "bg-emerald-500 text-white" : i === step ? "bg-indigo-600 text-white" : "bg-gray-200 text-gray-500")}>
                {i < step ? <Check className="w-3.5 h-3.5" /> : i + 1}
              </div>
              <span className={cn("text-xs font-medium hidden sm:block", i === step ? "text-ds-text" : "text-ds-text-muted")}>{s}</span>
            </div>
            {i < STEPS.length - 1 && <div className={cn("flex-1 h-px mx-3", i < step ? "bg-emerald-400" : "bg-ds-border")} />}
          </div>
        ))}
      </div>

      <div className="bg-white border border-ds-border rounded-card shadow-card p-6">
        {/* Step 1 — user details */}
        {step === 0 && (
          <div className="space-y-4">
            <h3 className="text-sm font-semibold text-ds-text mb-4">User details</h3>
            <div className="grid grid-cols-2 gap-4">
              {[{ key: "firstName", label: "First name", placeholder: "Sarah" }, { key: "lastName", label: "Last name", placeholder: "Chen" }].map(({ key, label, placeholder }) => (
                <div key={key}>
                  <label className="block text-xs font-medium text-ds-text-secondary mb-1.5">{label}</label>
                  <input value={form[key as keyof typeof form]}
                    onChange={(e) => setForm((f) => ({ ...f, [key]: e.target.value }))}
                    placeholder={placeholder}
                    className="w-full px-3 py-2 text-sm border border-ds-border rounded-lg focus:outline-none focus:border-indigo-400 focus:ring-1 focus:ring-indigo-400" />
                  {errors[key] && <p className="text-xs text-red-500 mt-1">{errors[key]}</p>}
                </div>
              ))}
              <div className="col-span-2">
                <label className="block text-xs font-medium text-ds-text-secondary mb-1.5">Work email</label>
                <input type="email" value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                  placeholder="sarah@acme.com"
                  className="w-full px-3 py-2 text-sm border border-ds-border rounded-lg focus:outline-none focus:border-indigo-400 focus:ring-1 focus:ring-indigo-400" />
                {errors.email && <p className="text-xs text-red-500 mt-1">{errors.email}</p>}
              </div>
              <div className="col-span-2">
                <label className="block text-xs font-medium text-ds-text-secondary mb-1.5">Job title <span className="text-ds-text-muted font-normal">(optional)</span></label>
                <input value={form.jobTitle} onChange={(e) => setForm((f) => ({ ...f, jobTitle: e.target.value }))}
                  placeholder="Oracle Functional Consultant"
                  className="w-full px-3 py-2 text-sm border border-ds-border rounded-lg focus:outline-none focus:border-indigo-400 focus:ring-1 focus:ring-indigo-400" />
              </div>
            </div>
          </div>
        )}

        {/* Step 2 — app access */}
        {step === 1 && (
          <div>
            <h3 className="text-sm font-semibold text-ds-text mb-1">App access</h3>
            <p className="text-xs text-ds-text-muted mb-4">Roles in the next step are filtered by your selection.</p>
            <div className="space-y-2">
              {typedModules.map((mod) => {
                const meta = MODULE_META[mod.moduleKey] ?? { icon: BarChart2, iconBg: "bg-gray-400" };
                const Icon = meta.icon;
                const selected = selectedApps.includes(mod.moduleKey);
                const locked = !mod.isAvailable;
                return (
                  <button key={mod.moduleId} disabled={locked} onClick={() => toggleApp(mod.moduleKey)}
                    className={cn("w-full flex items-center gap-3 p-3.5 rounded-xl border text-left transition-colors",
                      locked ? "opacity-50 cursor-not-allowed border-ds-border" :
                        selected ? "border-indigo-300 bg-indigo-50" : "border-ds-border hover:border-ds-border-strong hover:bg-ds-bg")}>
                    <div className={cn("w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0", meta.iconBg)}>
                      {locked ? <Lock className="w-4 h-4 text-white" /> : <Icon className="w-4 h-4 text-white" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-ds-text">{mod.moduleName}</p>
                      <p className="text-xs text-ds-text-muted">{locked ? "Not enabled for this enterprise" : (mod as { description?: string }).description ?? ""}</p>
                    </div>
                    <div className={cn("w-5 h-5 rounded-full border flex items-center justify-center flex-shrink-0",
                      selected ? "bg-indigo-600 border-indigo-600" : "border-ds-border-strong")}>
                      {selected && <Check className="w-3 h-3 text-white" />}
                    </div>
                  </button>
                );
              })}
            </div>
            {errors.apps && <p className="text-xs text-red-500 mt-2">{errors.apps}</p>}
          </div>
        )}

        {/* Step 3 — roles */}
        {step === 2 && (
          <div>
            <h3 className="text-sm font-semibold text-ds-text mb-1">Assign roles</h3>
            <p className="text-xs text-ds-text-muted mb-4">Roles filtered to match your selected apps. A user can hold multiple roles.</p>
            <div className="space-y-2">
              {availableRoles.map((role) => {
                const selected = selectedRoles.includes(role.id);
                return (
                  <button key={role.id} onClick={() => toggleRole(role.id)}
                    className={cn("w-full flex items-start gap-3 p-3.5 rounded-xl border text-left transition-colors",
                      selected ? "border-indigo-300 bg-indigo-50" : "border-ds-border hover:border-ds-border-strong hover:bg-ds-bg")}>
                    <div className={cn("w-5 h-5 rounded-full border flex items-center justify-center flex-shrink-0 mt-0.5",
                      selected ? "bg-indigo-600 border-indigo-600" : "border-ds-border-strong")}>
                      {selected && <Check className="w-3 h-3 text-white" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-ds-text">{role.name}</p>
                      <p className="text-xs text-ds-text-muted mt-0.5">{role.description}</p>
                    </div>
                  </button>
                );
              })}
              {availableRoles.length === 0 && (
                <p className="text-sm text-ds-text-muted text-center py-4">No roles available for the selected apps.</p>
              )}
            </div>
            {errors.roles && <p className="text-xs text-red-500 mt-2">{errors.roles}</p>}
          </div>
        )}

        {/* Step 4 — review */}
        {step === 3 && (
          <div>
            <h3 className="text-sm font-semibold text-ds-text mb-4">Review and add user</h3>
            <div className="divide-y divide-ds-border">
              {[
                { label: "Name", value: `${form.firstName} ${form.lastName}` },
                { label: "Email", value: form.email },
                { label: "Apps", value: selectedApps.map((a) => a.charAt(0).toUpperCase() + a.slice(1)).join(", ") },
                { label: "Roles", value: availableRoles.filter((r) => selectedRoles.includes(r.id)).map((r) => r.name).join(", ") },
              ].map(({ label, value }) => (
                <div key={label} className="flex justify-between py-2.5 text-sm">
                  <span className="text-ds-text-secondary">{label}</span>
                  <span className="text-ds-text font-medium text-right max-w-xs">{value}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      <div className="flex justify-between">
        <button onClick={step === 0 ? () => navigate("/enterprise/users") : back}
          className="flex items-center gap-1.5 px-4 py-2 rounded-lg border border-ds-border text-sm text-ds-text-secondary hover:bg-ds-bg">
          <ArrowLeft className="w-4 h-4" /> {step === 0 ? "Cancel" : "Back"}
        </button>
        <button onClick={step === 3 ? handleSubmit : next}
          disabled={invite.isPending}
          className="flex items-center gap-1.5 px-4 py-2 rounded-lg bg-indigo-600 text-white text-sm font-medium hover:bg-indigo-700 disabled:opacity-50">
          {step === 3
            ? invite.isPending ? "Adding..." : <><Send className="w-4 h-4" /> Add user</>
            : <>Continue <ArrowRight className="w-4 h-4" /></>}
        </button>
      </div>
    </div>
  );
}
